/**
 * Per-user in-app notification inbox (navbar bell) — session in-memory store.
 *
 * When `GET /api/notifications` is available, the server is the source of
 * truth (`replaceFromApi` with mode `replace`). This module holds the current
 * session copy for Vuex / mark-read / FCM upsert — it does NOT persist to
 * localStorage and must not be treated as an offline source of truth.
 */

/** Legacy key — cleared once so old persisted inbox is not reused. */
export const STORAGE_KEY = 'appNotifications.v1'

/** Session FCM/local rows kept when API list replaces inbox (see fcm-push.js). */
export const PRESERVED_SESSION_SOURCES = new Set(['fcm', 'hr_local'])

/** Cap retained items per user so memory stays bounded. */
const MAX_ITEMS = 100
/** Cap dismissed ids so Clear all survives poll re-ingest without unbounded growth. */
const MAX_DISMISSED = 200

function emptyRoot() {
  return { byUser: {} }
}

/** Session-only root (no localStorage). */
let memoryRoot = emptyRoot()

try {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(STORAGE_KEY)
  }
} catch (e) {
  // ignore
}

function readRoot() {
  if (!memoryRoot || typeof memoryRoot !== 'object') {
    memoryRoot = emptyRoot()
  }
  if (!memoryRoot.byUser || typeof memoryRoot.byUser !== 'object') {
    memoryRoot.byUser = {}
  }
  return memoryRoot
}

function writeRoot(root) {
  memoryRoot = root && typeof root === 'object' ? root : emptyRoot()
}

function readUserEmpId() {
  try {
    const userData = JSON.parse(localStorage.getItem('userData') || 'null')
    if (!userData) return null
    return userData.emp_id ?? userData.empId ?? null
  } catch (e) {
    return null
  }
}

function userBucket(root, empId) {
  const key = String(empId)
  if (!root.byUser[key] || typeof root.byUser[key] !== 'object') {
    root.byUser[key] = { items: [], dismissedIds: [] }
  }
  if (!Array.isArray(root.byUser[key].items)) {
    root.byUser[key].items = []
  }
  if (!Array.isArray(root.byUser[key].dismissedIds)) {
    root.byUser[key].dismissedIds = []
  }
  return root.byUser[key]
}

function isDismissed(bucket, id) {
  return !!(id && Array.isArray(bucket.dismissedIds) && bucket.dismissedIds.includes(String(id)))
}

function sortByCreatedAtDesc(items) {
  return [...items].sort((a, b) => {
    const ta = Date.parse(a.createdAt) || 0
    const tb = Date.parse(b.createdAt) || 0
    return tb - ta
  })
}

/**
 * @typedef {Object} AppNotification
 * @property {string} id
 * @property {string} type
 * @property {string} title
 * @property {string} message
 * @property {string} createdAt
 * @property {boolean} read
 * @property {string|null} [link]
 * @property {Object|null} [linkQuery]
 * @property {string} [icon]
 * @property {string} [variant]
 * @property {string} [source]
 * @property {Object} [meta]
 */

/**
 * @param {string|number|null} [empId]
 * @returns {AppNotification[]}
 */
export function listNotifications(empId = null) {
  const viewer = empId != null ? empId : readUserEmpId()
  if (viewer == null) return []
  const bucket = readRoot().byUser[String(viewer)]
  const items = bucket && Array.isArray(bucket.items) ? bucket.items : []
  return sortByCreatedAtDesc(items)
}

/**
 * Upsert one notification. Preserves `read` if the same id already exists
 * (unless `forceUnread` is set — used when a re-ping creates a new id).
 *
 * @param {AppNotification} notification
 * @param {{ empId?: string|number|null, forceUnread?: boolean }} [options]
 * @returns {AppNotification[]}
 */
export function upsertNotification(notification, options = {}) {
  if (!notification || !notification.id) return listNotifications(options.empId)
  const viewer = options.empId != null ? options.empId : readUserEmpId()
  if (viewer == null) return []

  const root = readRoot()
  const bucket = userBucket(root, viewer)
  // Skip ids the user cleared — until a new id appears (new ping / level).
  if (!options.forceUnread && isDismissed(bucket, notification.id)) {
    return sortByCreatedAtDesc(bucket.items)
  }
  const idx = bucket.items.findIndex(n => n && n.id === notification.id)
  if (idx >= 0) {
    const prev = bucket.items[idx]
    bucket.items[idx] = {
      ...prev,
      ...notification,
      // Keep the original createdAt so poll re-ingest does not bump sort order.
      createdAt: prev.createdAt || notification.createdAt,
      read: options.forceUnread ? false : (prev.read === true || notification.read === true),
      id: notification.id,
    }
  } else {
    bucket.items.unshift({
      icon: 'BellIcon',
      variant: 'light-warning',
      link: null,
      linkQuery: null,
      meta: {},
      source: 'local',
      ...notification,
      read: notification.read === true,
    })
  }

  bucket.items = sortByCreatedAtDesc(bucket.items).slice(0, MAX_ITEMS)
  writeRoot(root)
  return bucket.items
}

/**
 * @param {AppNotification[]} notifications
 * @param {{ empId?: string|number|null }} [options]
 */
export function upsertNotifications(notifications, options = {}) {
  if (!Array.isArray(notifications) || !notifications.length) {
    return listNotifications(options.empId)
  }
  let items = []
  notifications.forEach(n => {
    items = upsertNotification(n, options)
  })
  return items
}

/**
 * @param {string} id
 * @param {{ empId?: string|number|null }} [options]
 */
export function markNotificationRead(id, options = {}) {
  if (!id) return listNotifications(options.empId)
  const viewer = options.empId != null ? options.empId : readUserEmpId()
  if (viewer == null) return []
  const root = readRoot()
  const bucket = userBucket(root, viewer)
  bucket.items = bucket.items.map(n => (n && n.id === id ? { ...n, read: true } : n))
  writeRoot(root)
  return sortByCreatedAtDesc(bucket.items)
}

/**
 * @param {{ empId?: string|number|null }} [options]
 */
export function markAllNotificationsRead(options = {}) {
  const viewer = options.empId != null ? options.empId : readUserEmpId()
  if (viewer == null) return []
  const root = readRoot()
  const bucket = userBucket(root, viewer)
  bucket.items = bucket.items.map(n => (n ? { ...n, read: true } : n))
  writeRoot(root)
  return sortByCreatedAtDesc(bucket.items)
}

/**
 * Remove every inbox item for the viewer (Clear all). Remembered ids are
 * not re-inserted by poll synthesize until a new notification id appears.
 *
 * @param {{ empId?: string|number|null }} [options]
 * @returns {AppNotification[]}
 */
export function clearAllNotifications(options = {}) {
  const viewer = options.empId != null ? options.empId : readUserEmpId()
  if (viewer == null) return []
  const root = readRoot()
  const bucket = userBucket(root, viewer)
  const clearedIds = bucket.items.map(n => n && n.id).filter(Boolean).map(String)
  const prevDismissed = Array.isArray(bucket.dismissedIds) ? bucket.dismissedIds : []
  const dismissedIds = [...new Set([...clearedIds, ...prevDismissed])].slice(0, MAX_DISMISSED)
  root.byUser[String(viewer)] = { items: [], dismissedIds }
  writeRoot(root)
  return []
}

/**
 * Apply a trusted API payload to the in-memory session inbox.
 *
 * @param {AppNotification[]} apiItems
 * @param {{ empId?: string|number|null, mode?: 'merge'|'replace' }} [options]
 *   - `replace` (default when API is source of truth): inbox = server rows only
 *   - `merge`: keep non-API local items (legacy / partial probe)
 */
export function replaceFromApi(apiItems, options = {}) {
  const viewer = options.empId != null ? options.empId : readUserEmpId()
  if (viewer == null) return []
  const mode = options.mode === 'merge' ? 'merge' : 'replace'
  const root = readRoot()
  const bucket = userBucket(root, viewer)
  const prev = bucket.items
  const dismissedIds = Array.isArray(bucket.dismissedIds) ? bucket.dismissedIds : []
  const dismissedSet = new Set(dismissedIds.map(String))
  const prevById = {}
  prev.forEach(n => {
    if (n && n.id) prevById[n.id] = n
  })

  const fromApi = (Array.isArray(apiItems) ? apiItems : [])
    .filter(n => n && n.id && !dismissedSet.has(String(n.id)))
    .map(n => {
      const existing = prevById[n.id]
      const serverRead = n.read === true || n.isRead === true || !!n.readAt || !!n.read_at
      return {
        icon: 'BellIcon',
        variant: 'light-primary',
        link: null,
        linkQuery: null,
        meta: {},
        ...n,
        source: n.source || 'api',
        // Prefer server read; keep optimistic local read across a stale GET.
        read: serverRead || (existing && existing.read === true),
        createdAt: n.createdAt || n.created_at || n.timestamp || new Date().toISOString(),
        title: n.title || n.subject || 'Notification',
        message: n.message || n.body || n.subtitle || '',
      }
    })

  let nextItems = fromApi
  if (mode === 'merge') {
    const apiIds = new Set(fromApi.map(n => n.id))
    const localKeep = prev.filter(n => (
      n && n.id && !apiIds.has(n.id) && n.source && n.source !== 'api'
    ))
    nextItems = [...fromApi, ...localKeep]
  } else {
    // Replace mode: server is truth, but keep recent FCM/session rows not yet on API.
    const apiDedupe = new Set(fromApi.map(n => {
      if (n.meta && n.meta.dedupeKey) return String(n.meta.dedupeKey)
      return n.id ? String(n.id) : ''
    }).filter(Boolean))
    const sessionKeep = prev.filter(n => {
      if (!n || !n.id) return false
      if (!n.source || !PRESERVED_SESSION_SOURCES.has(n.source)) return false
      const key = (n.meta && n.meta.dedupeKey) ? String(n.meta.dedupeKey) : String(n.id)
      return !apiDedupe.has(key) && !apiDedupe.has(String(n.id))
    })
    if (sessionKeep.length) {
      nextItems = [...fromApi, ...sessionKeep]
    }
  }

  root.byUser[String(viewer)] = {
    items: sortByCreatedAtDesc(nextItems).slice(0, MAX_ITEMS),
    // Server dismiss is authoritative in replace mode — clear local dismiss
    // memory so a later re-delivered dedupeKey can appear again.
    dismissedIds: mode === 'replace' ? [] : dismissedIds,
  }
  writeRoot(root)
  return root.byUser[String(viewer)].items
}

export function unreadCount(empId = null) {
  return listNotifications(empId).filter(n => n && !n.read).length
}

export default {
  STORAGE_KEY,
  listNotifications,
  upsertNotification,
  upsertNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  clearAllNotifications,
  replaceFromApi,
  unreadCount,
}
